import {
  KpiBase,
  KpiMetricType
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi,
  EntryType,
  QuestionSettingCode
} from '../../../data/model/questionnaire'
import {
  getQuestionEntryPrefix,
  getRankChoiceRequisite,
  isSettingEnabled
} from '../../../utils/questionnaireUtils'
import {
  KpiSubjectKind,
  getAllowedKpiMetrics,
  getKpiMatrixRows,
  getKpiOptionLabel,
  getKpiOptions,
  getKpiRowLabel,
  getKpiSubjectKind,
  getOptionKpiMetric,
  getTrackedKpiOptions,
  isMatrixKpiSubject
} from './kpi.utils'
import { suggestKpiTicks } from './suggestKpiTicks'
import { KpiSaveInput } from './useKpiMutations'

export const KPI_LABEL_MAX_LENGTH = 50

export type KpiDialogTarget =
  | { kind: 'question' }
  | { kind: 'option'; optionId: string }
  | { kind: 'row'; rowId: string }
  | { kind: 'rows' }
  | { kind: 'edit'; kpiId: string }

export interface KpiDialogOption {
  id: string
  label: string
}

export interface KpiDialogModel {
  title: 'Mark as KPI' | 'Mark row as KPI' | 'Edit KPI'
  subjectKind: KpiSubjectKind
  /** "Q1 · How optimistic are you…?", plus the row, option or slider range. */
  contextLine: string
  questionRef: string
  /** The metrics on offer; with one, the dialog shows it as text, not a radio. */
  metricTypes: KpiMetricType[]
  /** RANKED_TOP: N goes up to the number of options, or to the places respondents rank if fewer. */
  maxTopN: number
  /** The options that can be ticked; empty for a slider and for a marked option. */
  options: KpiDialogOption[]
  /** Set when marking one option: it is the only one counted, and nothing is ticked. */
  fixedOption?: KpiDialogOption
  rowId?: string
  rowLabel?: string
  /** The KPI being edited. */
  kpi?: EntryKpi
  /** The entry's other KPIs, whose measures a new KPI may not repeat. */
  otherKpis: EntryKpi[]
  hasDontKnow: boolean
}

/** What a KPI counts, as the single-KPI and the matrix rows dialogs both choose it. */
export interface KpiMeasureDraft {
  metricType: KpiMetricType
  /** SHARE and RANKED_TOP. */
  ticks: string[]
  /** NET. Switching between Share and Net keeps both sets. */
  positive: string[]
  negative: string[]
  base: KpiBase
  /** The suggestion the ticks started from, so the hint can say so while they still match it. */
  suggested: string[]
}

export interface KpiDraft extends KpiMeasureDraft {
  /** RANKED_TOP. Kept while several options are ticked, which count first place only. */
  topN: number
  label: string
  /** Until the label is typed in, it follows the default for what is counted. An edit's stored label counts as typed unless it is that default. */
  labelEdited: boolean
}

const questionTitleOf = (entry: DraftQuestionnaireEntry) => {
  if (entry.entryType === EntryType.MatrixEntryType) {
    return entry.entryItem.matrixTitle?.title ?? ''
  }
  return entry.entryType === EntryType.QuestionEntryType
    ? entry.entryItem.question?.text ?? ''
    : ''
}

// The question's "rank N": respondents rank only their top N. None, or 0, when they rank every option.
const rankedPlacesOf = (entry: DraftQuestionnaireEntry) => {
  if (entry.entryType !== EntryType.QuestionEntryType) {
    return undefined
  }
  const places = Number.parseInt(
    getRankChoiceRequisite(entry.entryItem) ?? '',
    10
  )
  return places > 0 ? places : undefined
}

const sliderRangeOf = (entry: DraftQuestionnaireEntry) => {
  const range =
    entry.entryType === EntryType.QuestionEntryType
      ? entry.entryItem.questionScale?.range
      : undefined
  return ` (slider ${range?.min ?? 0}–${range?.max ?? 100})`
}

export const getKpiDialogOptions = (
  entry: DraftQuestionnaireEntry
): KpiDialogOption[] =>
  getTrackedKpiOptions(entry).map((option) => ({
    id: option.responseOptionLk,
    label: getKpiOptionLabel(option)
  }))

/** "Q1 · How optimistic are you…?" */
export const getKpiQuestionLine = (entry: DraftQuestionnaireEntry) =>
  `${getQuestionEntryPrefix(entry)} · ${questionTitleOf(entry)}`

export const hasDontKnow = (entry: DraftQuestionnaireEntry) =>
  (entry.entryType === EntryType.QuestionEntryType ||
    entry.entryType === EntryType.MatrixEntryType) &&
  isSettingEnabled(entry.entryItem.settingValues, QuestionSettingCode.DontKnow)

/** What the dialog shows for a target, or null when the target no longer exists. */
export const getKpiDialogModel = (
  entry: DraftQuestionnaireEntry,
  target: KpiDialogTarget
): KpiDialogModel | null => {
  const subjectKind = getKpiSubjectKind(entry)
  // Marking several rows has a dialog of its own.
  if (!subjectKind || target.kind === 'rows') {
    return null
  }
  const kpis = entry.kpis ?? []
  const kpi =
    target.kind === 'edit'
      ? kpis.find(({ kpiId }) => kpiId === target.kpiId)
      : undefined
  if (target.kind === 'edit' && !kpi) {
    return null
  }

  const isMatrix = isMatrixKpiSubject(subjectKind)
  let rowId: string | undefined
  if (target.kind === 'row') {
    rowId = target.rowId
  } else if (kpi?.rowId) {
    rowId = kpi.rowId
  }
  if (isMatrix !== !!rowId) {
    // A matrix KPI needs a row; nothing else may name one.
    return null
  }
  const row = getKpiMatrixRows(entry).find(
    ({ questionLk }) => questionLk === rowId
  )
  if (rowId && !row?.trackingId) {
    return null
  }

  const options = getKpiDialogOptions(entry)
  let fixedOption: KpiDialogOption | undefined
  let metricTypes = getAllowedKpiMetrics(subjectKind)
  if (target.kind === 'option') {
    const optionMetric = getOptionKpiMetric(subjectKind)
    fixedOption = options.find(({ id }) => id === target.optionId)
    if (!optionMetric || !fixedOption) {
      return null
    }
    metricTypes = [optionMetric]
  }

  const questionRef = getQuestionEntryPrefix(entry)
  let contextLine = getKpiQuestionLine(entry)
  if (row) {
    contextLine += ` · Row: ${getKpiRowLabel(row)}`
  } else if (fixedOption) {
    contextLine += ` · Option: ${fixedOption.label}`
  } else if (subjectKind === 'slider') {
    contextLine += sliderRangeOf(entry)
  }

  let title: KpiDialogModel['title'] = 'Mark as KPI'
  if (kpi) {
    title = 'Edit KPI'
  } else if (row) {
    title = 'Mark row as KPI'
  }

  const rankedPlaces =
    subjectKind === 'ranked' ? rankedPlacesOf(entry) : undefined
  const optionCount = getKpiOptions(entry).length

  return {
    title,
    subjectKind,
    contextLine,
    questionRef,
    metricTypes,
    maxTopN: Math.min(optionCount, rankedPlaces ?? optionCount),
    options: fixedOption || subjectKind === 'slider' ? [] : options,
    fixedOption,
    rowId,
    rowLabel: row ? getKpiRowLabel(row) : undefined,
    kpi,
    otherKpis: kpis.filter(({ kpiId }) => kpiId !== kpi?.kpiId),
    hasDontKnow: hasDontKnow(entry)
  }
}

const cutAtWord = (text: string, maxLength = KPI_LABEL_MAX_LENGTH) => {
  if (text.length <= maxLength) {
    return text
  }
  const cut = text.slice(0, maxLength + 1)
  const lastSpace = cut.lastIndexOf(' ')
  if (lastSpace > 0) {
    return cut.slice(0, lastSpace).trim()
  }
  // Never through an emoji, which takes two UTF-16 units, as the server cuts a row's label.
  const end = /[\uD800-\uDBFF]/.test(text.charAt(maxLength - 1))
    ? maxLength - 1
    : maxLength
  return text.slice(0, end)
}

/** A row's KPI is named after its row, as the server names one marked with others. */
export const rowKpiLabel = (rowLabel: string, metricType: KpiMetricType) =>
  cutAtWord(metricType === KpiMetricType.Net ? `Net ${rowLabel}` : rowLabel)

// The option name is shortened, never the suffix: N is what tells a top-3 KPI from a first-place one.
const rankedLabel = (optionLabel: string, topN: number) => {
  const suffix = topN > 1 ? ` in the top ${topN}` : ' ranked first'
  const name = cutAtWord(optionLabel, KPI_LABEL_MAX_LENGTH - suffix.length)
  return `${name}${suffix}`
}

const automaticKpiLabel = (
  model: KpiDialogModel,
  metricType: KpiMetricType,
  topN: number,
  option: KpiDialogOption | undefined
): string | undefined => {
  if (option && metricType === getOptionKpiMetric(model.subjectKind)) {
    return metricType === KpiMetricType.RankedTop
      ? rankedLabel(option.label, topN)
      : cutAtWord(option.label)
  }
  if (model.rowLabel !== undefined) {
    return rowKpiLabel(model.rowLabel, metricType)
  }
  return undefined
}

export const defaultKpiLabel = (
  model: KpiDialogModel,
  metricType: KpiMetricType,
  topN = 1
): string => automaticKpiLabel(model, metricType, topN, model.fixedOption) ?? ''

const automaticLabelOf = (
  model: KpiDialogModel,
  {
    metricType,
    topN,
    optionIds
  }: Pick<KpiSaveInput, 'metricType' | 'topN' | 'optionIds'>
) =>
  automaticKpiLabel(
    model,
    metricType,
    topN ?? 1,
    optionIds.length === 1
      ? model.options.find(({ id }) => id === optionIds[0])
      : undefined
  )

const SUGGESTED_FOR: KpiSubjectKind[] = ['singleSelect', 'matrixSingle']

/** The first metric on offer, with the ticks suggested from the option wording where they can be. */
export const suggestedKpiMeasure = ({
  subjectKind,
  metricTypes,
  options
}: Pick<
  KpiDialogModel,
  'subjectKind' | 'metricTypes' | 'options'
>): KpiMeasureDraft => {
  const suggestion = SUGGESTED_FOR.includes(subjectKind)
    ? suggestKpiTicks(options)
    : { positive: [], negative: [] }
  return {
    metricType: metricTypes[0],
    ticks: suggestion.positive,
    positive: suggestion.positive,
    negative: suggestion.negative,
    base: KpiBase.Answered,
    suggested: suggestion.positive
  }
}

export const initialKpiDraft = (model: KpiDialogModel): KpiDraft => {
  const { kpi } = model
  if (kpi) {
    return {
      metricType: kpi.metricType,
      ticks: kpi.optionIds,
      positive: kpi.optionIds,
      negative: kpi.negativeOptionIds ?? [],
      // Kept as saved, even above the places the question ranks now.
      topN: kpi.topN ?? 1,
      base: kpi.base,
      label: kpi.label ?? '',
      labelEdited: (kpi.label ?? '') !== automaticLabelOf(model, kpi),
      suggested: []
    }
  }
  const measure = suggestedKpiMeasure(model)
  return {
    ...measure,
    topN: 1,
    label: defaultKpiLabel(model, measure.metricType),
    labelEdited: false
  }
}

/** Re-derives a name not typed in after a change to what the draft counts; an edit's returns to its stored name once the change is undone. */
export const syncKpiDraftLabel = (
  model: KpiDialogModel,
  draft: KpiDraft
): KpiDraft => {
  if (draft.labelEdited) {
    return draft
  }
  const { kpi } = model
  let label: string
  if (!kpi) {
    label = defaultKpiLabel(model, draft.metricType, draft.topN)
  } else if (isKpiMeasureChanged(model, draft)) {
    label = automaticLabelOf(model, toNewKpiInput(model, draft)) ?? draft.label
  } else {
    label = kpi.label ?? ''
  }
  return { ...draft, label }
}

export const setKpiDraftMetric = (
  model: KpiDialogModel,
  draft: KpiDraft,
  metricType: KpiMetricType
): KpiDraft => syncKpiDraftLabel(model, { ...draft, metricType })

export const setKpiDraftTopN = (
  model: KpiDialogModel,
  draft: KpiDraft,
  topN: number
): KpiDraft => syncKpiDraftLabel(model, { ...draft, topN })

export const setKpiDraftBase = <D extends KpiMeasureDraft>(
  draft: D,
  base: KpiBase
): D => ({ ...draft, base })

export const setKpiDraftLabel = (draft: KpiDraft, label: string): KpiDraft => ({
  ...draft,
  label,
  labelEdited: true
})

export const toggle = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]

export const toggleKpiTick = <D extends KpiMeasureDraft>(
  draft: D,
  id: string
): D => ({ ...draft, ticks: toggle(draft.ticks, id) })

/** Ticking an option on one side of a Net clears it on the other. */
export const toggleKpiNetTick = <D extends KpiMeasureDraft>(
  draft: D,
  side: 'positive' | 'negative',
  id: string
): D => {
  const other = side === 'positive' ? 'negative' : 'positive'
  const ticking = !draft[side].includes(id)
  return {
    ...draft,
    [side]: toggle(draft[side], id),
    [other]: ticking ? draft[other].filter((x) => x !== id) : draft[other]
  }
}

// In option order, without ids whose option has gone.
const inOptionOrder = (
  { options }: Pick<KpiDialogModel, 'options'>,
  ids: string[]
) => options.map(({ id }) => id).filter((id) => ids.includes(id))

/** The ticked options of a Share or Net, as a save sends them. */
export const toKpiMeasureOptionIds = (
  model: Pick<KpiDialogModel, 'options'>,
  draft: KpiMeasureDraft
): Pick<KpiSaveInput, 'optionIds' | 'negativeOptionIds'> => {
  const isNet = draft.metricType === KpiMetricType.Net
  return {
    optionIds: inOptionOrder(model, isNet ? draft.positive : draft.ticks),
    negativeOptionIds: isNet ? inOptionOrder(model, draft.negative) : undefined
  }
}

const isOneRankedOption = (model: KpiDialogModel, draft: KpiDraft) =>
  draft.metricType === KpiMetricType.RankedTop &&
  (!!model.fixedOption || inOptionOrder(model, draft.ticks).length <= 1)

/** Top N counts one option, so with several ticked a ranked KPI counts first place only. */
export const canChooseKpiTopN = (model: KpiDialogModel, draft: KpiDraft) =>
  model.maxTopN > 1 && isOneRankedOption(model, draft)

/** An edit whose saved N is above the places the question ranks now: only a new name keeps it. */
export const isSavedKpiTopNAboveMax = (
  model: KpiDialogModel,
  draft: KpiDraft
) =>
  !!model.kpi &&
  (model.kpi.topN ?? 1) > model.maxTopN &&
  isOneRankedOption(model, draft)

const toNewKpiInput = (
  model: KpiDialogModel,
  draft: KpiDraft
): KpiSaveInput => {
  const measured = toKpiMeasureOptionIds(model, draft)
  let optionIds: string[]
  if (model.fixedOption) {
    optionIds = [model.fixedOption.id]
  } else if (draft.metricType === KpiMetricType.Average) {
    optionIds = []
  } else {
    optionIds = measured.optionIds
  }
  let topN: number | undefined
  if (draft.metricType === KpiMetricType.RankedTop) {
    topN = canChooseKpiTopN(model, draft) ? draft.topN : 1
  }
  return {
    rowId: model.rowId,
    metricType: draft.metricType,
    topN,
    base:
      draft.metricType === KpiMetricType.Average
        ? KpiBase.Answered
        : draft.base,
    optionIds,
    negativeOptionIds: measured.negativeOptionIds,
    label: draft.label.trim()
  }
}

const sameIds = (a: string[], b: string[]) => {
  const other = new Set(b)
  return new Set(a).size === other.size && a.every((id) => other.has(id))
}

// As the server compares them: ids as sets, and no top N or base meaning the default.
const sameMeasure = (a: KpiSaveInput, b: KpiSaveInput) =>
  a.metricType === b.metricType &&
  (a.rowId ?? null) === (b.rowId ?? null) &&
  (a.topN ?? 1) === (b.topN ?? 1) &&
  (a.base ?? KpiBase.Answered) === (b.base ?? KpiBase.Answered) &&
  sameIds(a.optionIds, b.optionIds) &&
  sameIds(a.negativeOptionIds ?? [], b.negativeOptionIds ?? [])

const storedKpiInput = (kpi: EntryKpi): KpiSaveInput => ({
  kpiId: kpi.kpiId,
  rowId: kpi.rowId,
  metricType: kpi.metricType,
  topN: kpi.topN,
  base: kpi.base,
  optionIds: kpi.optionIds,
  negativeOptionIds: kpi.negativeOptionIds,
  label: kpi.label
})

/** A KPI keeps what it counts from wave to wave, so an edit that changes it saves as a new KPI. */
export const isKpiMeasureChanged = (model: KpiDialogModel, draft: KpiDraft) =>
  !!model.kpi &&
  !sameMeasure(
    toNewKpiInput(model, initialKpiDraft(model)),
    toNewKpiInput(model, draft)
  )

/** Whether a KPI already counts what the input would, as the server's DUPLICATE_KPI compares them. */
export const countsSameAs = (kpi: EntryKpi, input: KpiSaveInput) =>
  sameMeasure(storedKpiInput(kpi), input)

const duplicateOf = (model: KpiDialogModel, input: KpiSaveInput) =>
  model.otherKpis.find((other) => countsSameAs(other, input))

/** The server refuses a new KPI that counts what another KPI on the entry already counts. */
export const getDuplicateKpi = (
  model: KpiDialogModel,
  draft: KpiDraft
): EntryKpi | undefined =>
  model.kpi && !isKpiMeasureChanged(model, draft)
    ? undefined
    : duplicateOf(model, toNewKpiInput(model, draft))

/** A new KPI has no kpiId; an edit keeps it only while just the label changes. */
export const toKpiSaveInput = (
  model: KpiDialogModel,
  draft: KpiDraft
): KpiSaveInput => {
  const { kpi } = model
  if (!kpi || isKpiMeasureChanged(model, draft)) {
    return toNewKpiInput(model, draft)
  }
  // The server refuses any other change under a kpiId, so the rest goes as stored.
  return { ...storedKpiInput(kpi), label: draft.label.trim() }
}

export const canSaveKpi = (model: KpiDialogModel, draft: KpiDraft): boolean => {
  const label = draft.label.trim()
  if (!label) {
    return false
  }
  const { kpi } = model
  if (kpi && !isKpiMeasureChanged(model, draft)) {
    return label !== (kpi.label ?? '').trim()
  }
  if (getDuplicateKpi(model, draft)) {
    return false
  }

  return hasKpiTicksFor(toNewKpiInput(model, draft), model.maxTopN)
}

/** Whether the ticks make a whole measure: a Net needs both sides, apart. */
export const hasKpiTicksFor = (
  input: Pick<
    KpiSaveInput,
    'metricType' | 'optionIds' | 'negativeOptionIds' | 'topN'
  >,
  maxTopN = 1
): boolean => {
  const negative = input.negativeOptionIds ?? []
  switch (input.metricType) {
    case KpiMetricType.Average:
      return true
    case KpiMetricType.Net:
      return (
        input.optionIds.length > 0 &&
        negative.length > 0 &&
        !input.optionIds.some((id) => negative.includes(id))
      )
    default:
      return input.optionIds.length > 0 && (input.topN ?? 1) <= maxTopN
  }
}
